/**
 * AdminTable — polished reusable data table with dynamic pagination
 *
 * Props:
 *   data          array    Full dataset (pagination is client-side)
 *   columns       array    [{ key, label, render?, className?, headerClass? }]
 *   loading       bool
 *   emptyTitle    string
 *   emptySubtitle string
 *   defaultPageSize number  (default 10)
 *   dataTestId    string
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Inbox } from 'lucide-react';

/* ── Pagination engine ─────────────────────────────────────────── */
function buildPages(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  pages.push(1);
  if (current > 4) pages.push('…');
  const start = Math.max(2, current - 1);
  const end   = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 3) pages.push('…');
  pages.push(total);
  return pages;
}

/* ── Pagination bar ────────────────────────────────────────────── */
function PaginationBar({ page, totalPages, pageSize, totalItems, onPage, onPageSize }) {
  const from = Math.min((page - 1) * pageSize + 1, totalItems);
  const to   = Math.min(page * pageSize, totalItems);
  const pages = buildPages(page, totalPages);

  const BtnPage = ({ label, icon: Icon, disabled, onClick, active }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-center transition-colors select-none"
      style={{
        height: '32px',
        minWidth: '32px',
        padding: '0 6px',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: active ? 600 : 500,
        border: 'none',
        backgroundColor: active ? '#eff6ff' : disabled ? 'transparent' : '#ffffff',
        color: active ? '#3b82f6' : disabled ? '#cbd5e1' : '#64748b',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {Icon ? <Icon size={13} /> : label}
    </button>
  );

  return (
    <div
      className="flex flex-col sm:flex-row items-center gap-3 px-6 py-4"
      style={{ borderTop: '1px solid #f1f5f9', backgroundColor: '#ffffff' }}
    >
      {/* Left: showing info + per-page */}
      <div className="flex items-center gap-3 text-xs" style={{ color: '#6B7280' }}>
        <span>
          Showing <span style={{ fontWeight: 600, color: '#374151' }}>{totalItems === 0 ? 0 : from}–{to}</span> of <span style={{ fontWeight: 600, color: '#374151' }}>{totalItems}</span>
        </span>
        <div className="flex items-center gap-1.5">
          <span>Rows:</span>
          <Select value={String(pageSize)} onValueChange={v => onPageSize(Number(v))}>
            <SelectTrigger
              className="h-7 text-xs"
              style={{ width: '64px', borderColor: '#E5E7EB', backgroundColor: '#FFFFFF', borderRadius: '8px' }}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB' }}>
              {[10, 25, 50, 100].map(n => (
                <SelectItem key={n} value={String(n)} className="text-xs">{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right: page buttons */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <BtnPage icon={ChevronsLeft}  disabled={page === 1}          onClick={() => onPage(1)}            />
          <BtnPage icon={ChevronLeft}   disabled={page === 1}          onClick={() => onPage(page - 1)}     />
          {pages.map((p, i) =>
            p === '…'
              ? <span key={`ellipsis-${i}`} className="px-1 text-xs" style={{ color: '#9CA3AF' }}>…</span>
              : <BtnPage key={p} label={p} active={p === page} onClick={() => onPage(p)} />
          )}
          <BtnPage icon={ChevronRight}  disabled={page === totalPages} onClick={() => onPage(page + 1)}     />
          <BtnPage icon={ChevronsRight} disabled={page === totalPages} onClick={() => onPage(totalPages)}   />
        </div>
      )}
    </div>
  );
}

/* ── Main DataTable ────────────────────────────────────────────── */
export default function AdminTable({
  data = [],
  columns = [],
  loading = false,
  emptyTitle = 'No data',
  emptySubtitle = '',
  defaultPageSize = 10,
  dataTestId,
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // Reset page on data change
  useEffect(() => { setPage(1); }, [data.length]);

  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));
  const rows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, page, pageSize]);

  const handlePageSize = (size) => { setPageSize(size); setPage(1); };

  return (
    <div
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden font-sans w-full"
      style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
      data-testid={dataTestId}
    >
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ borderCollapse: 'collapse', borderSpacing: 0 }}>
          {/* Header */}
          <thead>
            <tr className="bg-white border-b border-gray-200">
              {columns.map((col, ci) => (
                <th
                  key={col.key || ci}
                  className={col.headerClass || ''}
                  style={{
                    padding: '12px 20px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#6b7280',
                    whiteSpace: 'nowrap',
                    ...(col.width ? { width: col.width } : {}),
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    {col.label}
                    {col.label && col.key !== 'actions' && (
                      <svg width="10" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4 }}>
                        <path d="M7 15l5 5 5-5"/>
                        <path d="M7 9l5-5 5 5"/>
                      </svg>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b border-gray-100">
                  {columns.map((col, ci) => (
                    <td key={ci} style={{ padding: '16px 20px' }}>
                      <Skeleton className="h-4 w-full rounded" style={{ backgroundColor: '#F3F4F6' }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-gray-50"
                    >
                      <Inbox size={20} className="text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-700">{emptyTitle}</p>
                    {emptySubtitle && <p className="text-xs mt-1 text-gray-500">{emptySubtitle}</p>}
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row, ri) => (
                <tr
                  key={row.id || ri}
                  className="group transition-colors hover:bg-gray-50 border-b border-gray-100 last:border-0"
                >
                  {columns.map((col, ci) => (
                    <td
                      key={ci}
                      className={col.className || ''}
                      style={{ padding: '12px 20px', verticalAlign: 'middle', color: '#374151' }}
                    >
                      {col.render ? col.render(row) : (row[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      {!loading && (
        <PaginationBar
          page={page}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={data.length}
          onPage={setPage}
          onPageSize={handlePageSize}
        />
      )}
    </div>
  );
}
